import { Controller } from "@hotwired/stimulus"

// Base class for controllers wrapping a stateful jQuery plugin that mutates
// the DOM on init (DataTables, bootstrap-datepicker, tagsManager).
//
// Turbo Drive caches the current page for instant back/forward navigation,
// but it captures that snapshot on `turbo:before-cache` -- which fires
// *before* the outgoing page's elements are actually removed from the
// document, i.e. before Stimulus's own disconnect() runs. A plugin only
// torn down in disconnect() is torn down too late: the cached snapshot
// still has the plugin's initialized markup (and, for jQuery, its internal
// $.data() instance flags) baked in. Restoring that snapshot on back-
// navigation re-triggers connect() against an already-initialized element
// -- which is exactly what throws DataTables' "Cannot reinitialise
// DataTable" warning, and would silently no-op or double-bind handlers for
// plugins (bootstrap-datepicker, tagsManager) that guard re-init more
// quietly.
//
// Subclasses call `this.cleanupOnCache(fn)` once, from connect(), with
// whatever teardown fully reverses their init (destroy the plugin, remove
// injected DOM, clear jQuery data/handlers). It's guaranteed to run exactly
// once, on whichever comes first: turbo:before-cache, or this element's own
// disconnect() (a same-session removal that isn't a cache/restore at all,
// e.g. the containing content being replaced by a Turbo Stream).
export default class extends Controller {
  cleanupOnCache(fn) {
    this.cleanup = () => {
      if (this.cleaned) return
      this.cleaned = true
      fn()
    }
    document.addEventListener("turbo:before-cache", this.cleanup)
  }

  disconnect() {
    if (!this.cleanup) return

    // Stimulus fires disconnect() for ANY node removal it sees in a
    // MutationObserver batch -- including a same-document move, where the
    // node is removed from one parent and immediately reinserted under
    // another (its own source doesn't check isConnected before processing
    // removals, only before processing additions). That's exactly what a
    // jQuery plugin wrapping this element on init looks like to Stimulus:
    // DataTables detaches the <table> and reinserts it inside a generated
    // wrapper div. Only run real cleanup if the element has genuinely left
    // the document -- otherwise this fires mid-init and destroys the very
    // thing that's still connected and working.
    if (this.element.isConnected) return

    this.cleanup()
    document.removeEventListener("turbo:before-cache", this.cleanup)
  }
}
